import urlcat from 'urlcat';

import { type UUID } from '~/types/common';
import { type EntityType } from '~/types/entity';

import {
  DeliveryNoteAnalyticsChartName,
  UserAnalyticsChartName,
} from '~/data/analytics/useQueryAnalyticsChart/types';
import {
  DeliveryNoteKpiName,
  UserKpiName,
} from '~/data/analytics/useQueryAnalyticsKpi/types';

/**
 * Paths for all endpoints of the VESTIGAS API used in the customer app.
 */
export const ENDPOINT = {
  ANALYTICS: {
    GET_ACCOUNTING_REFERENCES_DETAILS: () =>
      'accounting_reference/statistics/details',
    GET_CHART: () => 'analytics/chart',
    GET_DELIVERY_NOTE_CHART: (chartName: DeliveryNoteAnalyticsChartName) =>
      urlcat('delivery_note/statistics/chart/:chartName', { chartName }),
    GET_DELIVERY_NOTE_DETAILS: () => 'delivery_note/statistics/details',
    GET_DELIVERY_NOTE_KPI: (kpiName: DeliveryNoteKpiName) =>
      urlcat('delivery_note/statistics/:kpiName', { kpiName }),
    GET_SITES_DETAILS: () => 'site/statistics/details',
    GET_TOP_ACCOUNTING_REFERENCES_BREAKDOWN: () =>
      'accounting_reference/statistics/top_accounting_references',
    GET_TOP_SITES_BREAKDOWN: () => 'site/statistics/top_sites',
    GET_USER_CHART: (chartName: UserAnalyticsChartName) =>
      urlcat('user/activity/chart/:chartName', { chartName }),
    GET_USER_DETAILS: () => 'user/activity/details',
    GET_USER_KPI: (kpiName: UserKpiName) =>
      urlcat('user/activity/:kpiName', { kpiName }),
  },
  ARTICLE: {
    CREATE: () => 'article_master/article',
    DELETE: (articleId: UUID) =>
      urlcat('article_master/article/:articleId', { articleId }),
    GET: (articleId: UUID) =>
      urlcat('article_master/article/:articleId', { articleId }),
    GET_ALL: () => 'article_master/article',
    UPDATE: (articleId: UUID) =>
      urlcat('article_master/article/:articleId', { articleId }),
  },
  CALL_OFF: {
    ACCEPT_CANCEL: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId/accept_cancel', { callOffId }),
    ACCEPT_CHANGES: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId/accept_changes', { callOffId }),
    BUYER_RESPOND: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId/magic_link/respond', { callOffId }),
    CANCEL: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId/cancel', { callOffId }),
    CREATE: () => 'calloffs',
    CREATE_ITEM: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId/items', { callOffId }),
    DECLINE_CHANGES: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId/decline_changes', { callOffId }),
    DELETE: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId', { callOffId }),
    DELETE_ITEM: (callOffId: UUID, itemId: UUID): string =>
      urlcat('calloffs/:callOffId/items/:itemId', { callOffId, itemId }),
    DENY_CANCEL: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId/deny_cancel', { callOffId }),
    DUPLICATE: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId/duplicate', { callOffId }),
    GET: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId', { callOffId }),
    GET_ALL: () => 'calloffs',
    GET_BUYER_ACTIONS: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId/magic_link/actions', { callOffId }),
    GET_DIFF: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId/diff', { callOffId }),
    GET_HISTORY: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId/history', { callOffId }),
    GET_ITEMS: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId/items', { callOffId }),
    GET_VERSION: (callOffId: UUID, versionNumber: number): string =>
      urlcat('calloffs/:callOffId/versions/:versionNumber', {
        callOffId,
        versionNumber,
      }),
    GET_VIA_MAGIC_LINK: (callOffId: UUID): string =>
      urlcat('calloffs/magic_link/:callOffId', { callOffId }),
    LOCK: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId/lock', { callOffId }),
    REQUEST_CANCEL: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId/request_cancel', { callOffId }),
    REQUEST_CHANGES: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId/request_changes', { callOffId }),
    RESEND_MAGIC_LINK: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId/magic_link/resend', { callOffId }),
    STORAGE_LOCATIONS: () => 'calloffs/storage_locations',
    SUPPLIER_CONTACTS: () => 'calloffs/supplier_contacts',
    SUPPLIER_RESPOND: (callOffId: UUID): string =>
      urlcat('calloffs/magic_link/:callOffId/respond', { callOffId }),
    UPDATE: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId', { callOffId }),
    UPDATE_ITEM: (callOffId: UUID, itemId: UUID): string =>
      urlcat('calloffs/:callOffId/items/:itemId', { callOffId, itemId }),
  },
  CALL_OFF_ATTACHMENT: {
    ADD: (callOffId: UUID): string =>
      urlcat('calloffs/:callOffId/attachments', { callOffId }),
    DELETE: (callOffId: UUID, attachmentId: UUID): string =>
      urlcat('calloffs/:callOffId/attachments/:attachmentId', {
        attachmentId,
        callOffId,
      }),
  },
  CATEGORY: {
    CREATE: () => 'article_master/category',
    DELETE: (categoryId: UUID) =>
      urlcat('article_master/category/:categoryId', { categoryId }),
    DELETE_ICON: (categoryIconId: UUID) =>
      urlcat('article_master/category/:categoryIconId/icon', {
        categoryIconId,
      }),
    GET: (categoryId: UUID) =>
      urlcat('article_master/category/:categoryId', { categoryId }),
    GET_ALL: () => 'article_master/category',
    GET_ICON: (categoryIconId: UUID) =>
      urlcat('article_master/category/:categoryIconId/icon', {
        categoryIconId,
      }),
    UPDATE: (categoryId: UUID) =>
      urlcat('article_master/category/:categoryId', { categoryId }),
    UPDATE_ICON: (categoryIconId: UUID) =>
      urlcat('article_master/category/:categoryIconId/icon', {
        categoryIconId,
      }),
  },
  COMPANY: {
    CREATE: () => 'company',
    DELETE_LOGO: (companyId: UUID): string =>
      urlcat('company/:companyId/logo', { companyId }),
    GET: (companyId: UUID): string =>
      urlcat('company/:companyId', { companyId }),
    GET_ALL: () => 'company',
    GET_ALL_LEGACY: () => 'company/all',
    GET_ALLOWED_BULK: (companyId: UUID) =>
      urlcat('company/bulk_allowed_companies', { company_id: companyId }),
    GET_AUTO_APPROVE_DLN_POSITIONS: (companyId: UUID): string =>
      urlcat('company/:companyId/auto_approve_dln_positions', { companyId }),
    GET_COMPANY_INVOICE_SENDING_SETTINGS: (companyId: UUID): string =>
      urlcat('company/:companyId/invoice_sending_settings', { companyId }),
    GET_DLN_TO_INVOICE: (companyId: UUID): string =>
      urlcat('company/:companyId/dln_to_invoice', { companyId }),
    GET_EMPLOYEES: (companyId: UUID): string =>
      urlcat('company/:companyId/employees', { companyId }),
    GET_INVOICE_PROTOCOL: (companyId: UUID): string =>
      urlcat('company/:companyId/invoice_protocol_to_invoice', { companyId }),
    GET_INVOICE_RECEIVERS: (companyId: UUID): string =>
      urlcat('company/:companyId/invoice_receivers', { companyId }),
    GET_LOGO: (companyId: UUID): string =>
      urlcat('company/:companyId/logo', { companyId }),
    GET_PUBLIC_INFO: (companyId: UUID): string =>
      urlcat('company/:companyId/public_info', { companyId }),
    UPDATE_AUTO_APPROVE_DLN_POSITIONS: (companyId: UUID): string =>
      urlcat('company/:companyId/auto_approve_dln_positions', { companyId }),
    UPDATE_COMPANY_DLN_TO_INVOICE: (companyId: UUID) =>
      urlcat('company/:companyId/dln_to_invoice', { companyId }),
    UPDATE_INVOICE_SENDING_SETTINGS: (companyId: UUID) =>
      urlcat('company/:companyId/invoice_sending_settings', { companyId }),
    UPLOAD_LOGO: (companyId: UUID): string =>
      urlcat('company/:companyId/logo', { companyId }),
    UPSERT: (companyId: UUID): string =>
      urlcat('company/:companyId', { companyId }),
  },
  CONNECTION_CHECK: {
    GET: (checkId: UUID): string =>
      urlcat('connection_checks/:checkId', { checkId }),
    START: () => 'connection_checks',
  },
  CONTRACT: {
    ACTION: (contractId: UUID): string =>
      urlcat('contracts/:contractId/action', { contractId }),
    ACTIONS: (contractId: UUID): string =>
      urlcat('contracts/:contractId/actions', { contractId }),
    CONFIRM: (contractId: UUID): string =>
      urlcat('contracts/:contractId/confirm', { contractId }),
    CREATE_DRAFT: () => 'contracts/drafts',
    CREATE_DRAFT_FROM_IMPORT_JOB: () => 'contracts/drafts/from_import_job',
    DELETE: (contractId: UUID): string =>
      urlcat('contracts/:contractId', { contractId }),
    DUPLICATE: (contractId: UUID): string =>
      urlcat('contracts/:contractId/duplicate', { contractId }),
    DUPLICATE_CHECK: () => 'contracts/duplicate_check',
    GET: (contractId: UUID): string =>
      urlcat('contracts/:contractId', { contractId }),
    GET_ALL: () => 'contracts',
    GET_ARTICLE_SUGGESTIONS: () => 'contracts/suggestions/articles',
    GET_BULK: () => 'contracts/query/ids',
    GET_CONDITION_NAME_SUGGESTIONS: () =>
      'contracts/suggestions/condition_names',
    GET_FILTER_PARTIES: () => 'contracts/filter_options/parties',
    GET_HISTORY: (contractId: UUID): string =>
      urlcat('contracts/:contractId/history', { contractId }),
    GET_HISTORY_VERSION_ITEM_CHANGES: (
      contractId: UUID,
      versionNumber: number,
    ): string =>
      urlcat('contracts/:contractId/history/:versionNumber/item_changes', {
        contractId,
        versionNumber,
      }),
    GET_HISTORY_VERSION_ITEMS: (
      contractId: UUID,
      versionNumber: number,
    ): string =>
      urlcat('contracts/:contractId/history/:versionNumber/items', {
        contractId,
        versionNumber,
      }),
    LINK_ACCOUNTING_REFERENCE: (contractId: UUID): string =>
      urlcat('contracts/:contractId/accounting-references', { contractId }),
    PERMISSIONS: (contractId: UUID): string =>
      urlcat('contracts/:contractId/permissions', { contractId }),
    PUBLIC_VIEW: (contractId: UUID): string =>
      urlcat('contracts/:contractId/public_view', { contractId }),
    UNLINK_ACCOUNTING_REFERENCE: (
      contractId: UUID,
      accountingReferenceId: UUID,
    ): string =>
      urlcat(
        'contracts/:contractId/accounting-references/:accountingReferenceId',
        {
          accountingReferenceId,
          contractId,
        },
      ),
    UPDATE: (contractId: UUID): string =>
      urlcat('contracts/:contractId', { contractId }),
  },
  CONTRACT_ATTACHMENT: {
    ADD: (contractId: UUID): string =>
      urlcat('contracts/:contractId/attachments', { contractId }),
    DELETE: (contractId: UUID, attachmentId: UUID): string =>
      urlcat('contracts/:contractId/attachments/:attachmentId', {
        attachmentId,
        contractId,
      }),
  },
  CONTRACT_IMPORT_JOB: {
    COMPLETE: (jobId: UUID): string =>
      urlcat('contracts/import_jobs/:jobId/complete', { jobId }),
    CREATE: () => 'contracts/import_jobs',
    DELETE: (jobId: UUID): string =>
      urlcat('contracts/import_jobs/:jobId', { jobId }),
    GET: (jobId: UUID): string =>
      urlcat('contracts/import_jobs/:jobId', { jobId }),
    GET_ALL: () => 'contracts/import_jobs',
  },
  CONTRACT_ITEM: {
    CREATE: (contractId: UUID): string =>
      urlcat('contracts/:contractId/items', { contractId }),
    DEACTIVATE: (contractId: UUID, itemId: UUID): string =>
      urlcat('contracts/:contractId/items/:itemId/deactivate', {
        contractId,
        itemId,
      }),
    DELETE: (contractId: UUID, itemId: UUID): string =>
      urlcat('contracts/:contractId/items/:itemId', { contractId, itemId }),
    GET: (contractId: UUID, itemId: UUID): string =>
      urlcat('contracts/:contractId/items/:itemId', { contractId, itemId }),
    GET_ALL: (contractId: UUID): string =>
      urlcat('contracts/:contractId/items', { contractId }),
    GET_NET_PRICE: (contractId: UUID, itemId: UUID): string =>
      urlcat('contracts/:contractId/items/:itemId/net_price', {
        contractId,
        itemId,
      }),
    REACTIVATE: (contractId: UUID, itemId: UUID): string =>
      urlcat('contracts/:contractId/items/:itemId/reactivate', {
        contractId,
        itemId,
      }),
    UPDATE: (contractId: UUID, itemId: UUID): string =>
      urlcat('contracts/:contractId/items/:itemId', { contractId, itemId }),
  },
  COST_CENTER: {
    CREATE: () => 'accounting_reference',
    GET: (costCenterId: UUID): string =>
      urlcat('accounting_reference/:costCenterId', { costCenterId }),
    GET_ALL: () => 'accounting_reference',
    GET_ALL_LEGACY: () => 'accounting_reference/all',
    GET_BULK: () => 'accounting_reference/query/bulk',
    UPDATE: (costCenterId: UUID): string =>
      urlcat('accounting_reference/:costCenterId', { costCenterId }),
  },
  CUSTOM_FIELD: {
    CREATE: () => 'custom_field',
    CREATE_OPTION: (customFieldId: UUID) =>
      urlcat('custom_field/:customFieldId/option', { customFieldId }),
    DELETE: (customFieldId: UUID) =>
      urlcat('custom_field/:customFieldId', { customFieldId }),
    DELETE_OPTION: (customFieldId: UUID, customFieldOptionId: UUID) =>
      urlcat('custom_field/:customFieldId/option/:customFieldOptionId', {
        customFieldId,
        customFieldOptionId,
      }),
    GET: (customFieldId: UUID) =>
      urlcat('custom_field/:customFieldId', { customFieldId }),
    GET_ALL: () => 'custom_field/all',
    GET_ALL_LEGACY: () => 'custom_field/all',
    GET_BULK: () => 'custom_field/query/bulk',
    GET_OPTION: (customFieldId: UUID, customFieldOptionId: UUID) =>
      urlcat('custom_field/:customFieldId/option/:customFieldOptionId', {
        customFieldId,
        customFieldOptionId,
      }),
    GET_OPTIONS: (customFieldId: UUID) =>
      urlcat('custom_field/:customFieldId/option', { customFieldId }),
    SEARCH: () => 'custom_field',
    UPDATE: (customFieldId: UUID) =>
      urlcat('custom_field/:customFieldId', { customFieldId }),
    UPDATE_OPTIONS: (customFieldId: UUID) =>
      urlcat('custom_field/:customFieldId/options', {
        customFieldId,
      }),
  },
  CUSTOMER_SUBSCRIPTION: {
    CREATE: () => 'customer_subscription',
    GET_ALL: () => 'customer_subscription',
    TEST: () => 'customer_subscription/test',
    UPDATE: (customerSubscriptionId: UUID) =>
      urlcat('customer_subscription/:customerSubscriptionId', {
        customerSubscriptionId,
      }),
  },
  DATA_EXCHANGE: {
    CREATE: () => 'data_exchange',
    CREATE_CONTACT_ATTEMPT: () => 'data_exchange/contact_attempt',
    CREATE_EMAIL_WITH_REGISTRATION_LINK: () => 'data_exchange/contact_link',
    GET: (dataExchangeId: UUID) =>
      urlcat('data_exchange/query/:dataExchangeId', {
        dataExchangeId,
      }),
    GET_ALL_LEGACY: () => 'data_exchange/all',
    GET_COMPANIES: () => 'data_exchange/company_information/all',
    GET_COMPANIES_ALL: () => 'data_exchange/company_information/query',
    GET_COMPANY: (companyId: UUID) =>
      urlcat('data_exchange/company_information/:companyId', { companyId }),
    GET_COMPANY_ACCOUNT: (companyAccountId: UUID) =>
      urlcat('data_exchange/company_accounts/:companyAccountId', {
        companyAccountId,
      }),
    GET_COMPANY_ACCOUNTS: () => 'data_exchange/company_accounts',
    GET_DATA_EXCHANGE_COMPANIES: () => 'data_exchange/companies',
    GET_DATA_EXCHANGE_COMPANY: (companyId: UUID) =>
      urlcat('data_exchange/companies/:companyId', { companyId }),
    GET_ELIGIBLE_RESPONSIBLE_PERSONS: (dataExchangeId: UUID) =>
      urlcat('data_exchange/:dataExchangeId/eligible_responsible_persons', {
        dataExchangeId,
      }),
    GET_EXCEL_DATA: () => 'data_exchange/company_information/excel',
    SET_RESPONSIBLE_PERSON: () => 'data_exchange/responsible_user',
  },
  DATA_EXPORT: {
    CREATE: () => 'data_export/create',
    CREATE_DELIVERY_NOTE_PDF_EXPORT: () => 'delivery_note/pdf_exports',
    CREATE_INVOICE_FILES: () => 'invoices/file_exports',
    GET_RESULT: (jobId: UUID) =>
      urlcat('data_export/result/:jobId', { jobId }) as string,
  },
  DATA_SUBSCRIPTION: {
    CREATE_CONTACT_POINT: () => 'data_subscription/contact_point',
    CREATE_DATA_SUBSCRIPTION: () => 'data_subscription',
    CREATE_NOTIFICATION_POLICY: () => 'data_subscription/notification_policy',
    DELETE_CONTACT_POINT: (contactPointId: UUID) =>
      urlcat('data_subscription/contact_point/:contactPointId', {
        contactPointId,
      }),
    DELETE_DATA_SUBSCRIPTION: (dataSubscriptionId: UUID) =>
      urlcat('data_subscription/:dataSubscriptionId', { dataSubscriptionId }),
    DELETE_NOTIFICATION_POLICY: () =>
      'data_subscription/notification_policy/delete',
    GET: () => 'data_subscription',
    GET_CONTACT_POINTS: () => 'data_subscription/contact_point',
    GET_EXECUTION_LOG: () => 'data_subscription/execution_log',
    GET_EXECUTION_LOG_DETAIL: (logId: UUID) =>
      urlcat('data_subscription/execution_log/:logId', { logId }),
    GET_NOTIFICATION_POLICY: () => 'data_subscription/notification_policy',
    RERUN_EXECUTION_LOG: () => 'data_subscription/execution_log/rerun',
    RUN_CONTACT_POINT: (contactPointId: UUID) =>
      urlcat('data_subscription/contact_point/:contactPointId/run', {
        contactPointId,
      }),
    TEST_SEND_CONTACT_POINT: (contactPointId: UUID) =>
      urlcat('data_subscription/contact_point/:contactPointId/test_send', {
        contactPointId,
      }),
    UPDATE_CONTACT_POINT: (contactPointId: UUID) =>
      urlcat('data_subscription/contact_point/:contactPointId', {
        contactPointId,
      }),
    UPDATE_DATA_SUBSCRIPTION: (contactPointId: UUID) =>
      urlcat('data_subscription/:contactPointId', { contactPointId }),
  },
  DATA_SYNC: {
    CREATE_JOB: () => 'data_sync/sync_job',
    CREATE_JOB_FROM_TEMPLATE: () => 'data_sync/sync_job/from_template',
    CREATE_TASK: () => 'data_sync/sync_task',
    DELETE_JOB: (jobId: UUID) => urlcat('data_sync/sync_job/:jobId', { jobId }),
    DELETE_TASK: (taskId: UUID) =>
      urlcat('data_sync/sync_task/:taskId', { taskId }),
    GET_ALL_JOBS: () => 'data_sync/sync_job',
    GET_ALL_TASKS: () => 'data_sync/sync_task',
    GET_JOB: (jobId: UUID) => urlcat('data_sync/sync_job/:jobId', { jobId }),
    GET_TASK: (taskId: UUID) =>
      urlcat('data_sync/sync_task/:taskId', { taskId }),
    GET_TEMPLATES: () => 'data_sync/templates',
    RUN_TASK_MANUAL: (taskId: UUID) =>
      urlcat('data_sync/sync_task/:taskId/manual', { taskId }),
    UPDATE_JOB: (jobId: UUID) => urlcat('data_sync/sync_job/:jobId', { jobId }),
    UPDATE_TASK: (taskId: UUID) =>
      urlcat('data_sync/sync_task/:taskId', { taskId }),
  },
  DELIVERY_NOTE: {
    APPROVE_ITEM: (assetId: UUID, itemId: number) =>
      urlcat('delivery_note/:assetId/item/:itemId/approve', {
        assetId,
        itemId,
      }),
    APPROVE_PARSING_CASE: (caseId: UUID) =>
      urlcat('delivery_note/parsing_cases/:caseId/actions/approve', { caseId }),
    ARCHIVE_PARSING_CASE: (caseId: UUID) =>
      urlcat('delivery_note/parsing_cases/:caseId/actions/archive', { caseId }),
    BILL_ITEM: (assetId: UUID, itemId: number) =>
      urlcat('delivery_note/:assetId/item/:itemId/bill', {
        assetId,
        itemId,
      }),
    BULK_APPROVE_DLNS: () => 'delivery_note/bulk/approve',
    BULK_APPROVE_ITEMS: (assetId: UUID) =>
      urlcat('delivery_note/:assetId/item/bulk/approve', { assetId }),
    BULK_BILL_DLNS: () => 'delivery_note/bulk/bill',
    BULK_BILL_ITEMS: (assetId: UUID) =>
      urlcat('delivery_note/:assetId/item/bulk/bill', { assetId }),
    CHECK_DUPLICATE: () => 'asset/delivery_note_duplicate_check',
    CREATE: () => 'asset/manual',
    CREATE_PARSING_CASE: () => 'delivery_note/parsing_cases',
    CREATE_SIGNATURE_REQUESTS: () => 'delivery_note/signature_requests',
    CREATE_TEMPLATE: () => 'dln_template',
    CREATE_VESTIGAS_FORMAT: () => 'asset/vestigas_dln',
    DELETE_TEMPLATE: () => 'dln_template/delete',
    GET: (deliveryNoteId: UUID): string =>
      urlcat('asset/delivery_note/:deliveryNoteId', { deliveryNoteId }),
    GET_ALL: () => 'asset/delivery_note/search',
    GET_ALL_ARTICLE_TEMPLATES: () => 'article_master/all',
    GET_ALL_PARSING_CASES: () => 'delivery_note/parsing_cases',
    GET_ANALYTICS: () => 'analytics',
    GET_ANALYTICS_EXPORT: (requestId: UUID) =>
      urlcat('analytics/report/export/:requestId', { requestId }),
    GET_ARTICLE_SUGGESTIONS: (): string => 'delivery_note/suggestions/articles',
    GET_ASSET_EXPORT: (requestId: UUID) =>
      urlcat('asset/delivery_note/export/:requestId', { requestId }),
    GET_ATTACHMENT: (chainId: UUID, digest: string): string =>
      urlcat('asset/delivery_note/:chainId/attachment', { chainId, digest }),
    GET_AVAILABLE_PDF_TYPES: (assetId: UUID) =>
      urlcat('asset/:assetId/available_pdf_types', { assetId }),
    GET_CHAINS: (): string => 'asset/delivery_note/ids/chains',
    GET_CONTRACT_SUGGESTIONS: (assetId: UUID): string =>
      urlcat('delivery_note/:assetId/contract_suggestions', { assetId }),
    GET_DLN_NUMBER_SUGGESTION: (): string =>
      'delivery_note/suggestions/dln_number',
    GET_ESG_DATA: () => 'asset/delivery_note/items/esg_values',
    GET_FILTER_SUGGESTIONS: () => 'analytics/suggestions',
    GET_HANDWRITTEN_SIGNATURE: (chainId: UUID): string =>
      urlcat('asset/delivery_note/chain/:chainId/handwritten_signature', {
        chainId,
      }),
    GET_INTERNAL: (assetId: UUID) =>
      urlcat('delivery_note/:assetId/internal', { assetId }),
    GET_NOTES_BY_IDS: () => 'asset/delivery_note/ids',
    GET_PARSING_CASE: (caseId: UUID) =>
      urlcat('delivery_note/parsing_cases/:caseId', { caseId }),
    GET_PARSING_CASE_FORM_CONFIG: () =>
      'delivery_note/parsing_cases/form_config',
    GET_REPORT: () => 'analytics/report',
    GET_SEARCH_COUNT: () => 'asset/delivery_note/search/count',
    GET_SHARED_WITH_USERS: (deliveryNoteId: UUID) =>
      urlcat('asset/:deliveryNoteId/share', { deliveryNoteId }),
    GET_SIGNATURE_REQUESTS: (deliveryNoteId: UUID) =>
      urlcat('delivery_note/:deliveryNoteId/signature_requests', {
        deliveryNoteId,
      }),
    GET_SIMPLE_FORMAT: (deliveryNoteId: UUID): string =>
      urlcat('asset/delivery_notes/:deliveryNoteId', { deliveryNoteId }),
    GET_SUPPLIER_SUGGESTIONS: (): string =>
      'delivery_note/suggestions/suppliers',
    GET_SYNC_GATEWAY_CHANGES: (since: number) =>
      urlcat('_changes', {
        feed: 'longpoll',
        include_docs: true,
        since,
      }), // endpoint uses query parameters in snake case
    GET_TEMPLATES: () => 'dln_template',
    LINK_CONTRACT: (assetId: UUID): string =>
      urlcat('delivery_note/:assetId/contract', { assetId }),
    OPEN_IN_MOBILE_APP: () => 'push/open_dln',
    QUERY_NOTES_BY_IDS: () => 'asset/delivery_note/ids/query',
    QUERY_SIGNATURE_REQUESTS: () => 'delivery_note/signature_requests/query',
    REMOVE_MAPPING: (deliveryNoteId: UUID): string =>
      urlcat('asset/:deliveryNoteId/direct_mapping', {
        deliveryNoteId,
      }),
    REQUEST_ANALYTICS_EXPORT: () => 'analytics/report/export/request',
    REQUEST_ASSET_EXPORT: () => 'asset/delivery_note/export/request',
    REQUEST_SIGNATURE: () => 'push/request_signature',
    RESTORE_PARSING_CASE: (caseId: UUID) =>
      urlcat('delivery_note/parsing_cases/:caseId/actions/restore', { caseId }),
    SHARE: (assetId: UUID) => urlcat('asset/:assetId/share', { assetId }),
    UPDATE_ARTICLE_TEMPLATE: () => 'article_master',
    UPDATE_DIRECT_MAPPING: (deliveryNoteId: UUID) =>
      urlcat('asset/:deliveryNoteId/direct_mapping', { deliveryNoteId }),
    UPDATE_TEMPLATE: () => 'dln_template',
    VALIDATE: () => 'asset/validate',
  },
  EMAIL_ALIAS: {
    ACTIVATE: (emailAliasId: UUID) =>
      urlcat('email_aliases/:emailAliasId/activate', { emailAliasId }),
    CREATE: () => 'email_aliases',
    DEACTIVATE: (emailAliasId: UUID) =>
      urlcat('email_aliases/:emailAliasId', { emailAliasId }),
    GET_BY_ACCOUNTING_REFERENCE: () => 'email_aliases/by_accounting_reference',
  },
  EXPORT_RULE: {
    CREATE: () => 'export_rules',
    DELETE: (ruleId: UUID): string =>
      urlcat('export_rules/:ruleId', { ruleId }),
    GET: (ruleId: UUID): string => urlcat('export_rules/:ruleId', { ruleId }),
    GET_ALL: () => 'export_rules',
    UPDATE: (ruleId: UUID): string =>
      urlcat('export_rules/:ruleId', { ruleId }),
  },
  FILE: {
    GET_UPLOAD_LINK: () => 'files/upload_link',
  },
  IGNORED_ARTICLES: {
    CREATE: () => 'invoice_verification/ignored_article',
    DELETE: (ignoredArticleId: UUID) =>
      urlcat('invoice_verification/ignored_article/:ignoredArticleId', {
        ignoredArticleId,
      }),
    GET_ALL: () => 'invoice/ignored_article',
    UPDATE: (ignoredArticleId: UUID) =>
      urlcat('invoice_verification/ignored_article/:ignoredArticleId', {
        ignoredArticleId,
      }),
  },
  INVOICE: {
    APPROVE: (invoiceId: UUID): string =>
      urlcat('invoices/:invoiceId/approve', { invoiceId }),
    ATTACH_USER_FILES: (invoiceId: UUID): string =>
      urlcat('invoices/:invoiceId/user_files', { invoiceId }),
    CREATE_COMMENT: (invoiceId: UUID): string =>
      urlcat('invoices/:invoiceId/comments', { invoiceId }),
    DELETE_ADDITIONAL_NOTE: (invoiceId: UUID) =>
      urlcat('invoice/:invoiceId/additional_note', { invoiceId }),
    DELETE_ITEM_ADDITIONAL_NOTE: (invoiceId: UUID, invoiceItemId: number) =>
      urlcat('invoice/:invoiceId/item/:invoiceItemId/additional_note', {
        invoiceId,
        invoiceItemId,
      }),
    EDIT_DLN_ITEM_LINKS: (invoiceId: UUID): string =>
      urlcat('invoices/:invoiceId/dln_item_links', { invoiceId }),
    GET_ALL: () => 'invoice',
    GET_ALL_V2: () => 'invoices',
    GET_ASSIGNEE_SUGGESTIONS: () => 'invoices/suggestions/assignees',
    GET_BUYER_SUGGESTIONS: () => 'invoices/suggestions/buyers',
    GET_COST_CENTER_SUGGESTIONS: () => 'invoices/suggestions/cost_centers',
    GET_DETAIL: (invoiceId: UUID): string =>
      urlcat('invoices/:invoiceId', { invoiceId }),
    GET_EMAIL: (invoiceId: UUID, fileId: UUID) =>
      urlcat('invoice/:invoiceId/file/:fileId', { invoiceId, fileId }),
    GET_EMAIL_SUGGESTION: (invoiceId: UUID, kind: string): string =>
      urlcat('invoices/:invoiceId/email_suggestions/:kind', {
        invoiceId,
        kind,
      }),
    GET_EXPORT_ATTEMPT: (invoiceId: UUID, attemptId: UUID): string =>
      urlcat('invoices/:invoiceId/export_attempts/:attemptId', {
        attemptId,
        invoiceId,
      }),
    GET_FILE_BY_ID: (invoiceId: UUID, fileId: UUID) =>
      urlcat('invoices/:invoiceId/file/:fileId', { invoiceId, fileId }),
    GET_FILE_BY_ROLE: (invoiceId: UUID) =>
      urlcat('invoices/:invoiceId/file', { invoiceId }),
    GET_FILE_BY_TYPE: (invoiceId: UUID) =>
      urlcat('invoice/:invoiceId/file', { invoiceId }),
    GET_FILTER_PARTIES: () => 'invoice/filter_options/parties',
    GET_INTERNAL: (invoiceId: UUID) =>
      urlcat('invoice/internal/:invoiceId', { invoiceId }),
    GET_ISSUER_SUGGESTIONS: () => 'invoices/suggestions/issuers',
    GET_LINKABLE_DLN_ITEMS: (invoiceId: UUID, lineItemId: UUID): string =>
      urlcat('invoices/:invoiceId/line_items/:lineItemId/linkable_dln_items', {
        invoiceId,
        lineItemId,
      }),
    GET_LINKABLE_DLN_ITEMS_SELECTION: (
      invoiceId: UUID,
      lineItemId: UUID,
    ): string =>
      urlcat(
        'invoices/:invoiceId/line_items/:lineItemId/linkable_dln_items/selection',
        { invoiceId, lineItemId },
      ),
    GET_LINKABLE_DELIVERY_NOTES: (invoiceId: UUID): string =>
      urlcat('invoices/:invoiceId/linkable_delivery_notes', { invoiceId }),
    GET_OWNER_COMPANY_SUGGESTIONS: () => 'invoices/suggestions/owner_companies',
    GET_POSSIBLE_ASSIGNEES: (invoiceId: UUID) =>
      urlcat('invoices/:invoiceId/possible_assignees', { invoiceId }),
    GET_SITE_SUGGESTIONS: () => 'invoices/suggestions/sites',
    GET_TIMELINE: (invoiceId: UUID): string =>
      urlcat('invoices/:invoiceId/timeline', { invoiceId }),
    IMPORT: () => 'invoices/import',
    PROPOSE_DELIVERY_NOTE_ASSIGNMENT: (invoiceId: UUID): string =>
      urlcat('invoices/:invoiceId/delivery_note_assignments/proposal', {
        invoiceId,
      }),
    QUERY_PARTY_COMPANIES: () => 'invoices/companies/query',
    REJECT: (invoiceId: UUID): string =>
      urlcat('invoices/:invoiceId/reject', { invoiceId }),
    REMOVE_DLN_ITEM_LINK: (
      invoiceId: UUID,
      lineItemId: UUID,
      linkId: UUID,
    ): string =>
      urlcat(
        'invoices/:invoiceId/line_items/:lineItemId/dln_item_links/:linkId',
        { invoiceId, lineItemId, linkId },
      ),
    REPLACE_ASSIGNEES: (invoiceId: UUID) =>
      urlcat('invoices/:invoiceId/assignees', { invoiceId }),
    RETRIGGER_CHECK_RUN: (invoiceId: UUID): string =>
      urlcat('invoices/:invoiceId/check_run', { invoiceId }),
    RETRY_EXPORT_ATTEMPT: (invoiceId: UUID, attemptId: UUID): string =>
      urlcat('invoices/:invoiceId/export_attempts/:attemptId/retry', {
        attemptId,
        invoiceId,
      }),
    UPDATE_DATA_APPROVED: (invoiceId: UUID): string =>
      urlcat('invoices/:invoiceId/data/approved', { invoiceId }),
    UPDATE_LINE_ITEM: (invoiceId: UUID, lineItemId: UUID): string =>
      urlcat('invoices/:invoiceId/line_items/:lineItemId', {
        invoiceId,
        lineItemId,
      }),
    UPDATE_LINE_ITEM_APPROVED: (invoiceId: UUID, lineItemId: UUID): string =>
      urlcat('invoices/:invoiceId/line_items/:lineItemId/approved', {
        invoiceId,
        lineItemId,
      }),
    UPSERT_ADDITIONAL_NOTE: (invoiceId: UUID) =>
      urlcat('invoice/:invoiceId/additional_note', { invoiceId }),
    UPSERT_ITEM_ADDITIONAL_NOTE: (invoiceId: UUID, invoiceItemId: number) =>
      urlcat('invoice/:invoiceId/item/:invoiceItemId/additional_note', {
        invoiceId,
        invoiceItemId,
      }),
  },
  INVOICE_EMAIL: {
    GET_ALL: () => 'invoice_emails',
    GET_DETAIL: (emailId: UUID): string =>
      urlcat('invoice_emails/:emailId', { emailId }),
  },
  LEDGER_ACCOUNT: {
    CREATE: () => 'ledger_accounts',
    DELETE: (ledgerAccountId: UUID): string =>
      urlcat('ledger_accounts/:ledgerAccountId', { ledgerAccountId }),
    GET: (ledgerAccountId: UUID): string =>
      urlcat('ledger_accounts/:ledgerAccountId', { ledgerAccountId }),
    GET_ALL: () => 'ledger_accounts',
    IMPORT: () => 'ledger_accounts/import',
    UPDATE: (ledgerAccountId: UUID): string =>
      urlcat('ledger_accounts/:ledgerAccountId', { ledgerAccountId }),
    VALIDATE_UNIQUENESS: () => 'ledger_accounts/validate_uniqueness',
  },
  MATCHING_RULE: {
    CREATE: () => 'matching_rule',
    DELETE: (matchingRuleId: UUID) =>
      urlcat('matching_rule/:matchingRuleId', { matchingRuleId }),
    GET: (matchingRuleId: UUID) =>
      urlcat('matching_rule/:matchingRuleId', { matchingRuleId }),
    GET_FOR_ACCOUNTING_REFERENCE: (accountingReferenceId: UUID) =>
      urlcat('accounting_reference/:accountingReferenceId/matching_rules', {
        accountingReferenceId,
      }),
    GET_VALUE_PROPOSAL: () => 'matching_rule/value_proposal',
    UPDATE: (matchingRuleId: UUID) =>
      urlcat('matching_rule/:matchingRuleId', { matchingRuleId }),
  },
  ORGANIZATIONAL_UNIT: {
    ADD_TO: (organizationalUnitId: UUID, entityType: EntityType): string =>
      urlcat('access_control/org_unit/:organizationalUnitId/:entityType', {
        entityType,
        organizationalUnitId,
      }),
    CREATE: () => 'access_control/org_unit',
    DELETE: (organizationalUnitId: UUID): string =>
      urlcat('access_control/org_unit/:organizationalUnitId', {
        organizationalUnitId,
      }),
    DELETE_FROM: (
      organizationalUnitId: UUID,
      entityType: EntityType,
      entityId: UUID,
    ): string =>
      urlcat(
        'access_control/org_unit/:organizationalUnitId/:entityType/:entityId',
        {
          entityId,
          entityType,
          organizationalUnitId,
        },
      ),
    GET: (organizationalUnitId: UUID): string =>
      urlcat('access_control/org_unit/:organizationalUnitId', {
        organizationalUnitId,
      }),
    GET_ALL: () => 'access_control/org_unit/test_all',
    UPDATE: (organizationalUnitId: UUID): string =>
      urlcat('access_control/org_unit/:organizationalUnitId', {
        organizationalUnitId,
      }),
  },
  PARTNER_SETTINGS: {
    CREATE: () => 'partner_settings',
    DELETE: (settingId: UUID) =>
      urlcat('partner_settings/:settingId', { settingId }),
    GET_ALL: () => 'partner_settings',
    IMPORT_CONFIRM: () => 'partner_settings/import/confirm',
    IMPORT_PREVIEW: () => 'partner_settings/import/preview',
    UPDATE: (settingId: UUID) =>
      urlcat('partner_settings/:settingId', { settingId }),
  },
  PDF_TEMPLATES: {
    CREATE: () => 'pdf_template',
    CREATE_TEST_PDF_TEMPLATE: () => 'asset/dev/request_test_pdf_template',
    DELETE: (templateId: UUID) =>
      urlcat('pdf_template/:templateId', { templateId }),
    GET: (templateId: UUID) =>
      urlcat('pdf_template/:templateId', { templateId }),
    GET_ALL: () => 'pdf_template',
    GET_TEST_PDF_TEMPLATE: () => 'asset/dev/get_test_pdf_template',
  },
  PERMISSION: {
    CREATE: (
      subjectType: Extract<EntityType, 'user' | 'user_group'>,
      subjectId: UUID,
      targetType: EntityType,
      targetId: UUID,
    ): string =>
      urlcat(
        'access_control/granted_permissions/:subjectType/:subjectId/:targetType/:targetId',
        {
          subjectId,
          subjectType,
          targetId,
          targetType,
        },
      ),
    DELETE: (permissionGrantId: UUID): string =>
      urlcat('access_control/granted_permissions/:permissionGrantId', {
        permissionGrantId,
      }),
    UPDATE: (permissionGrantId: UUID): string =>
      urlcat('access_control/granted_permissions/:permissionGrantId', {
        permissionGrantId,
      }),
  },
  PRODUCT_PRICE: {
    BULK_DELETE: () => 'invoice/product_price/bulk',
    CREATE: () => 'invoice/product_price',
    DELETE: (productPriceId: UUID) =>
      urlcat('invoice/product_price/:productPriceId', { productPriceId }),
    GET: (productPriceId: UUID) =>
      urlcat('invoice/product_price/:productPriceId', { productPriceId }),
    GET_ALL: () => 'invoice/product_price',
    IMPORT: () => 'invoice/product_price/import',
    UPDATE: (productPriceId: UUID) =>
      urlcat('invoice/product_price/:productPriceId', { productPriceId }),
  },
  RELEASE_NOTES: {
    GET: (fileName: string) =>
      urlcat('release-notes/:fileName', {
        fileName,
      }),
  },
  ROLE: {
    CREATE: () => 'access_control/role',
    DELETE: (roleId: UUID) => urlcat('access_control/role/:roleId', { roleId }),
    GET: (roleId: UUID) => urlcat('access_control/role/:roleId', { roleId }),
    GET_ALL: () => 'access_control/role',
    SET_DEFAULT_ROLE: (roleId: UUID) =>
      urlcat('access_control/role/:roleId/default_role', { roleId }),
    UPDATE: (roleId: UUID) => urlcat('access_control/role/:roleId', { roleId }),
  },
  SIGNATURE_FIELD: {
    CREATE: () => 'signature_field',
    DELETE: (signatureFieldId: UUID) =>
      urlcat('signature_field/:signatureFieldId', { signatureFieldId }),
    GET_ALL: () => 'signature_field/all',
    UPDATE: (signatureFieldId: UUID) =>
      urlcat('signature_field/:signatureFieldId', { signatureFieldId }),
  },
  SITE: {
    BLACKLIST_SITE_IDENTIFIER: (siteId: UUID, identifierUuid: UUID) =>
      urlcat('site/:siteId/:identifierUuid', { siteId, identifierUuid }),
    COUNT_AFFECTED_DLNS: (supplierId: UUID) =>
      urlcat('site/identifier/:supplierId/affected_dlns/count', { supplierId }),
    CREATE: () => 'site',
    CREATE_SITES_AND_COST_CENTERS_VIA_CSV: () => 'admin/site_cc_csv',
    CREATE_VIA_CSV: () => 'site/import_csv',
    DELETE_IDENTIFIER: (siteId: UUID, identifierUuid: UUID) =>
      urlcat('site/:siteId/identifier/:identifierUuid', {
        identifierUuid,
        siteId,
      }),
    GET: (siteId: UUID): string => urlcat('site/:siteId', { siteId }),
    GET_ALL: () => 'site',
    GET_ALL_LEGACY: () => 'site/all',
    GET_BULK: () => 'site/query/bulk',
    GET_IDENTIFIER_PROPOSALS: () => 'site/identifier/proposals',
    IDENTIFIER_FOR_SUPPLIER: (siteId: UUID) =>
      urlcat('site/:siteId/identifier', { siteId }),
    PROPOSAL_IDENTIFIER: (siteId: UUID) =>
      urlcat('site/:siteId/identifier/proposal', { siteId }),
    UPDATE: (siteId: UUID): string => urlcat('site/:siteId', { siteId }),
    UPDATE_ACCOUNTING_REFERENCES: (siteId: UUID) =>
      urlcat('site/:siteId/accounting_reference', { siteId }),
    UPDATE_COST_CENTERS: (siteId: UUID): string =>
      urlcat('site/:siteId/accounting_reference', { siteId }),
    UPDATE_IDENTIFIER: () => 'site/identifier',
  },
  SUPPLIER: {
    ADD_CONTACT_LINK: () => 'data_exchange/contact_link',
    ADD_NETWORK_SUPPLIER: () => 'suppliers/network/add',
    ADD_RESPONSIBLE_PERSON: (deId: UUID) =>
      urlcat('suppliers/:deId/responsible_persons', { deId }),
    CANCEL_ACTIVATION: (reqId: UUID) =>
      urlcat('suppliers/:reqId/activation/cancel', { reqId }),
    CHECK_IDENTIFIER: () => 'suppliers/requests/check',
    CREATE_CONTACT: (deId: UUID) =>
      urlcat('suppliers/:deId/contacts', { deId }),
    DELETE_CONTACT: (deId: UUID, contactId: UUID) =>
      urlcat('suppliers/:deId/contacts/:contactId', { deId, contactId }),
    DELETE_CONTACT_LINK: (linkId: UUID) =>
      urlcat('data_exchange/contact_link/delete/:linkId', { linkId }),
    DELETE_RESPONSIBLE_PERSON: (deId: UUID, userId: UUID) =>
      urlcat('suppliers/:deId/responsible_persons/:userId', {
        deId,
        userId,
      }),
    FIND: () => 'suppliers/find',
    GET_ACTIVATION_PUBLIC: (reqId: UUID) =>
      urlcat('suppliers/activation/:reqId', { reqId }),
    GET_ACTIVATION_REQUEST: (reqId: UUID) =>
      urlcat('suppliers/:reqId/activation', { reqId }),
    GET_ACTIVATION_REQUESTS: (deId: UUID) =>
      urlcat('suppliers/:deId/activation/requests', { deId }),
    GET_ALL_CONTACT_LINKS: () => 'data_exchange/contact_link/all',
    GET_CONTACT_LINK: (linkId: UUID) =>
      urlcat('data_exchange/contact_link/:linkId', { linkId }),
    GET_CONTACT_LINK_BY_DATA_EXCHANGE_ID: (deId: UUID) =>
      urlcat('suppliers/:deId/contact_link', { deId }),
    GET_DETAIL: (deId: UUID) => urlcat('suppliers/:deId', { deId }),
    GET_HISTORY: (deId: UUID) => urlcat('suppliers/:deId/history', { deId }),
    GET_NETWORK: () => 'suppliers/network',
    GET_OVERVIEW: () => 'suppliers/overview',
    GET_OVERVIEW_COUNTS: () => 'suppliers/counts',
    GET_PRIORITY: () => 'suppliers/priority',
    GET_REQUESTS: () => 'suppliers/requests',
    GET_RESPONSIBLE_PERSON_OPTIONS: () => 'suppliers/responsible_persons',
    POST_ACTIVATION: (deId: UUID) =>
      urlcat('suppliers/:deId/activation', { deId }),
    POST_ACTIVATION_PLAN: (deId: UUID) =>
      urlcat('suppliers/:deId/activation/plan', { deId }),
    POST_RELEVANCE: () => 'suppliers/relevance',
    PUT_PRIORITY: () => 'suppliers/priority',
    REQUEST_NEW: () => 'suppliers/requests',
    RESEND_ACTIVATION: (reqId: UUID) =>
      urlcat('suppliers/:reqId/activation/resend', { reqId }),
    RESPOND_ACTIVATION_PUBLIC: (reqId: UUID) =>
      urlcat('suppliers/activation/:reqId/respond', { reqId }),
    UPDATE_ACTIVATION: (reqId: UUID) =>
      urlcat('suppliers/:reqId/activation', { reqId }),
    UPDATE_CONTACT: (deId: UUID, contactId: UUID) =>
      urlcat('suppliers/:deId/contacts/:contactId', { deId, contactId }),
    UPDATE_CONTACT_LINK_ACCESS: () => 'data_exchange/contact_link',
    UPDATE_RELATIONSHIP: (deId: UUID) => urlcat('suppliers/:deId', { deId }),
    UPDATE_RESPONSIBLE_PERSON: (deId: UUID, userId: UUID) =>
      urlcat('suppliers/:deId/responsible_persons/:userId', {
        deId,
        userId,
      }),
  },
  TAX_CODE: {
    BULK_DELETE: () => 'tax_codes/bulk_delete',
    CREATE: () => 'tax_codes',
    DELETE: (taxCodeId: UUID) => urlcat('tax_codes/:taxCodeId', { taxCodeId }),
    FILTER_OPTIONS: () => 'tax_codes/filter_options',
    GET: (taxCodeId: UUID) => urlcat('tax_codes/:taxCodeId', { taxCodeId }),
    GET_ALL: () => 'tax_codes',
    UPDATE: (taxCodeId: UUID) => urlcat('tax_codes/:taxCodeId', { taxCodeId }),
  },
  UNITS: {
    GET_ALL: () => 'units/dictionary',
    GET_MAPPING_BY_ISSUER_AND_IDENTIFIER: () => 'units/unit_mapping/query',
  },
  UNSUBSCRIBE: {
    CONFIRM: () => 'unsubscribe/confirm',
    RESOLVE: () => 'unsubscribe',
  },
  USER: {
    COMPANY_ACCOUNT: () => 'user/company_account',
    CREATE_ADMIN: () => 'admin/users',
    CREATE_VIA_CSV: () => 'admin/users_csv',
    DELETE_PROFILE_PICTURE: () => 'user/profile_picture',
    GET: (userId: UUID): string => urlcat('user/:userId', { userId }),
    GET_ALL: () => 'user',
    GET_BULK: () => 'user/query/bulk',
    GET_COMPANY: (userId: UUID): string =>
      urlcat('user/:userId/company', { userId }),
    GET_COMPANY_FOR_CURRENT_USER: () => 'user/company',
    GET_DATA: (includeCompanyInfo: boolean): string =>
      urlcat('user/data', { include_company_info: includeCompanyInfo }), // endpoint uses query parameter 'include_company_info'!
    GET_PROFILE_PICTURE: () => 'user/profile_picture',
    GET_PUSH_NOTIFICATION_SETTINGS: (userId: UUID): string =>
      urlcat('user/notification/settings', { user_id: userId }), // endpoint uses query parameter 'user_id'!
    GET_SETTINGS: (userId: UUID): string =>
      urlcat('admin/user/:userId/settings', { userId }),
    GET_SIGNATURE_ROLES: (userId: UUID): string =>
      urlcat('user/:userId/signature_roles', { userId }),
    SAVED_FILTER: (savedFilterId: UUID): string =>
      urlcat('user/saved_filters/:savedFilterId', { savedFilterId }),
    SAVED_FILTERS: () => 'user/saved_filters',
    UPDATE: (userId: UUID): string => urlcat('admin/users/:userId', { userId }), // endpoint uses query parameter 'user_id'!
    UPDATE_NAME: () => 'user/name',
    UPDATE_POSITION: () => 'user/position',
    UPDATE_PROFILE_PICTURE: () => 'user/profile_picture',
    UPDATE_PUSH_NOTIFICATION_SETTINGS: (userId: UUID): string =>
      urlcat('user/notification/settings', { user_id: userId }), // endpoint uses query parameter 'user_id'!
    UPDATE_SETTINGS: (userId: UUID): string =>
      urlcat('admin/user/:userId/settings', { userId }),
    UPDATE_SIGNATURE_ROLES: (userId: UUID): string =>
      urlcat('user/:userId/signature_roles', { userId }),
    UPDATE_USER_SETTINGS: () => 'user/settings',
  },
  USER_GROUP: {
    ADD_TO: (
      userGroupId: UUID,
      memberType: Extract<EntityType, 'user' | 'user_group'>,
    ): string =>
      urlcat('access_control/user_group/:userGroupId/:memberType', {
        memberType,
        userGroupId,
      }),
    CREATE: (): string => 'access_control/user_group',
    DELETE_FROM: (
      userGroupId: UUID,
      memberType: Extract<EntityType, 'user' | 'user_group'>,
      memberId: UUID,
    ): string =>
      urlcat('access_control/user_group/:userGroupId/:memberType/:memberId', {
        memberId,
        memberType,
        userGroupId,
      }),
    GET: (userGroupId: UUID): string =>
      urlcat('access_control/user_group/:userGroupId', { userGroupId }),
    GET_ALL: () => 'access_control/user_group/test_all',
    UPDATE: (userGroupId: UUID): string =>
      urlcat('access_control/user_group/:userGroupId', { userGroupId }),
  },
  VEHICLE: {
    CREATE: () => 'vehicle',
    GET: (vehicleId: UUID): string =>
      urlcat('vehicle/:vehicleId', { vehicleId }),
    GET_ALL: () => 'vehicle',
    UPDATE: (vehicleId: UUID): string =>
      urlcat('vehicle/:vehicleId', { vehicleId }),
  },
} as const;
